import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import OutsideClickHandler from "react-outside-click-handler";

import { ReactComponent as ArrowDownThingSvg } from "../../../assets/icons/top-arrow-noline-primary.svg";

import "../../../styles/comboBox.scss";
import { useField, useFormikContext } from "formik";

const ComboBox = (props) => {
  const {
    className,
    name,
    placeholder,
    defaultValue,
    defaultToggled,
    width,
    label,
    options,
    onChange,
    maxWidth,
    multi,
    translation,
    leadingIcon,
    onSearch,
    canClear,
    disabled,
    style,
  } = props;

  const formikContext = useFormikContext();

  const isInsideFormik = !!formikContext;

  const [field, meta] = formikContext
    ? useField(props)
    : [undefined, undefined];

  const [leadingWidth, setLeadingWidth] = useState(0);
  const [searchResetted, setSearchResetted] = useState(true);

  const leadingIconRef = useCallback((node) => {
    if (node === null) return;
    setLeadingWidth(node.offsetWidth);
  }, []);

  const [opts, setOptions] = useState(options ?? []);
  const [selected, setSelected] = useState(
    opts.find((o) => o.value === defaultValue || o.value === meta?.initialValue)
  );
  const [selections, setSelections] = useState(
    defaultValue ?? meta?.initialValue ?? []
  );

  // console.log(`${field?.name} meta`, meta);

  const [search, setSearch] = useState(!multi ? selected?.name : "");
  const [toggleOptions, setToggleOptions] = useState(defaultToggled);

  const classNames = [className, "combobox"];
  const selectedArrowClassNames = ["combobox-selected__arrow"];

  if (toggleOptions)
    selectedArrowClassNames.push("combobox-selected__arrow--toggled");

  const _disabled = isInsideFormik && (formikContext?.isSubmitting || disabled);

  // * Initial useEffect
  useEffect(() => {
    setOptions(options ?? []);
  }, [options]);

  // * Handle when meta value changed
  useEffect(() => {
    if (!formikContext) {
      return;
    }

    if (multi && meta.value) {
      setSelections(meta.value ?? []);
      return;
    }

    if (meta.value) {
      const _selected = options.find((o) => o.value === meta?.value);

      if (!_selected) {
        return;
      }

      setSelected(_selected);
      setSearch(_selected.name);
      return;
    }

    setSelected(undefined);
    setSearch("");
  }, [formikContext, meta?.value, selections, selected]);

  const handleSetTouched = (touched) => {
    formikContext?.setFieldTouched(field?.name, true);
  };

  const handleToggleOption = () => {
    if (_disabled) {
      setToggleOptions(false);
      return;
    }

    // * Check going to toggle close
    const newToggle = !toggleOptions;

    if (!newToggle && !meta?.touched) {
      handleSetTouched(true);
    }

    setToggleOptions(newToggle);
  };

  const handleSelect = (e) => {
    if (!e) {
      return;
    }

    const { value, name } = e;

    setSearchResetted(false);

    if (multi && selections?.includes(value)) {
      let _selections = selections.filter((s) => s !== value);
      setSelections(_selections);

      // * Set field value
      if (formikContext && field?.name) {
        formikContext.setFieldValue(field?.name, _selections);
        return;
      }

      onChange({ name, value: _selections });
      return;
    }

    if (multi && !selections?.includes(value)) {
      const _selections = [...selections, value];
      setSelections(_selections);
      // setToggleOptions(false);

      // * Set field value
      if (formikContext && field?.name) {
        formikContext.setFieldValue(field?.name, _selections);
        return;
      }

      onChange({ name, value: _selections });
      return;
    }

    // * Set field value
    if (formikContext && field?.name) {
      formikContext.setFieldValue(field?.name, value);
    }

    if (!formikContext) {
      onChange({ name, value });
    }

    // * Single option
    setSearch(name);
    setSelected(e);
    setOptions(options);
    setToggleOptions(false);
  };

  const handleOutsideClick = () => {
    if (!toggleOptions) return;
    handleSetTouched(true);
    handleToggleOption();
  };

  if (meta?.error && meta?.touched) {
    classNames.push("combobox--error");
  }

  // * Styles
  const _style = style ?? {};
  if (width) _style.width = width;
  if (maxWidth) _style.maxWidth = maxWidth;

  const optionStyles = {};

  if (label) {
    optionStyles.top = "78px";
  }

  if (!label) {
    optionStyles.top = "48px";
  }

  const handleSearchBeforeInput = () => {
    if (searchResetted) return;

    setSearchResetted(true);
    setSearch("");
  };

  // * On change value
  const handleOnChangeSearch = ({ target: { value } }) => {
    if (searchResetted) {
      setSearch(value);
    }

    // * Use external function if provided
    if (onSearch) {
      onSearch(value);
      return;
    }

    if (multi) {
    }

    if (!value) {
      setOptions(options);
      return;
    }

    // * Filter options
    setOptions(
      options.filter((o) => {
        // * Create a new RegExp object with the 'i' flag for case-insensitive matching
        const regex = new RegExp(value, "i");
        // * Test if the inputString matches the pattern
        return regex.test(o.name);
      })
    );
  };

  // * Render selected value
  const getSelectedRender = () => {
    // * Case single selection
    if (!multi) {
      const _selected = opts.find((o) => o.value === selected?.value);

      // console.log(`${field?.name} selected`, _selected);

      const { icon } = _selected || {};

      return (
        <>
          {icon && (
            <img
              className="combobox-selected-selection-icon"
              src={icon}
              height={24}
              width={24}
              style={{ objectFit: "cover" }}
            />
          )}
          <input
            className="combobox-selected-selection__input"
            value={search}
            onChange={handleOnChangeSearch}
            onBeforeInputCapture={handleSearchBeforeInput}
            placeholder={placeholder}
          />
        </>
      );
    }

    if (!selections || selections.length === 0) {
      return <span className="combobox__placeholder">{placeholder}</span>;
    }

    const _selections = selections.map((s) => (
      <div className="combobox-selected-selection-label" key={s.value}>
        <span className="combobox-selected-selection-label__span">
          {translation
            ? translation(opts.find((o) => o.value === s)?.name)
            : opts.find((o) => o.value === s)?.name}
        </span>
        {/* <div
          className="combobox-selected-selection-label__remove"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveMultiSelection(s);
          }}
        >
          x
        </div> */}
      </div>
    ));

    return _selections;
  };

  // * Handle clearing selections
  const handleClearSelections = (e) => {
    e.stopPropagation();

    if (multi) {
      setSelections([]);
      onChange([]);
      return;
    }

    setSelected(undefined);
    setSearch("");
    onChange(undefined);
  };

  if (multi) classNames.push("combobox--multi");
  if (disabled) classNames.push("combobox--disabled");

  return (
    <div className={classNames.join(" ")} style={_style}>
      <OutsideClickHandler onOutsideClick={handleOutsideClick}>
        {label && (
          <div
            className="combobox-label"
            style={{ paddingLeft: leadingIcon ? leadingWidth : 0 }}
          >
            {leadingIcon ? (
              <div
                className="combobox__icon combobox__icon-leading"
                ref={leadingIconRef}
              >
                {leadingIcon}
              </div>
            ) : (
              <></>
            )}
            {label}
          </div>
        )}
        <div
          className="combobox-selected"
          onClick={handleToggleOption}
          // style={style}
        >
          <span className="combobox-selected-selection">
            {getSelectedRender()}
          </span>
          {(selected || selections?.length > 0) && canClear && (
            <span
              className="combobox-selected__clear"
              onClick={handleClearSelections}
            >
              Clear
            </span>
          )}
          <ArrowDownThingSvg className={selectedArrowClassNames.join(" ")} />
        </div>
        {toggleOptions && (
          <div className="combobox-options-scroller" style={optionStyles}>
            <div
              className={`combobox-options ${
                label ? "combobox-options--with-label" : ""
              }`}
            >
              {opts.length === 0 && (
                <div
                  className={`combobox-options__option`}
                  key={`option-${-1}`}
                >
                  <span className="combobox-options__option-label">
                    No options
                  </span>
                </div>
              )}
              {opts.map((option, index) => {
                const isSelected = !multi
                  ? option.value === selected
                  : selections?.includes(option.value);

                const { name, value, icon } = option;

                const _optionClassNames = ["combobox-options__option"];

                if (isSelected)
                  _optionClassNames.push("combobox-options__option--selected");

                return (
                  <div
                    key={value}
                    className={_optionClassNames.join(" ")}
                    onClick={() => handleSelect(option)}
                  >
                    {multi && (
                      <input
                        className="combobox-options__option-checkbox"
                        type="checkbox"
                        name={field?.name}
                        checked={isSelected}
                        readOnly
                      />
                    )}
                    {icon && (
                      <img
                        className="combobox-options__option-icon"
                        src={icon}
                        height={24}
                        width={24}
                        style={{ objectFit: "cover" }}
                      />
                    )}
                    <span className="combobox-options__option-label">
                      {translation ? translation(name) : name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="combobox-footer">
          {meta?.error && meta?.touched && (
            <div className="combobox-footer-error">{meta.error}</div>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

ComboBox.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.object,
  defaultToggled: PropTypes.bool,
  /**
   * `[{ name, value  }]`
   */
  options: PropTypes.array,
  width: PropTypes.any,
  label: PropTypes.string,
  onChange: PropTypes.func,
  maxWidth: PropTypes.number,
  multi: PropTypes.bool,
  leadingIcon: PropTypes.element,
  /**
   * Whether to display clear button or not
   */
  canClear: PropTypes.bool,
};

ComboBox.defaultProps = {
  className: "",
  placeholder: "Please select",
  defaultValue: undefined,
  defaultToggled: false,
  with: "auto",
  onChange: (_value) => {},
  multi: false,
  canClear: false,
};

export default ComboBox;
